import { lazy, Suspense, useEffect } from 'react';
import { Route, Router, Switch, useLocation, useRoute } from 'wouter';
import { allowNavigationWithUnsavedChanges } from '@/hooks/use-unsaved-changes-guard';
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { queryClient, clearCacheAndReloadData } from '@/lib/queryClient';
import {
  CRM_QUERY_CACHE_BUSTER,
  CRM_QUERY_CACHE_MAX_AGE_MS,
  createCrmQueryPersister,
  shouldPersistCrmQuery,
} from '@/lib/persistedCrmQueryCache';
import AppLayout from '@/components/layout/AppLayout';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import EmergencyDebugButton from '@/components/debug/EmergencyDebugButton';
import { Toaster } from '@/components/ui/toaster';
import { useToast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { canonicalizeNotificationLink } from '@/lib/notificationTarget';
import { MARTINA_OPERATIONAL_QUEUE_COUNT_QUERY_KEY } from '@/hooks/useMartinaOperatorNotificationCount';
import { EmailAccountProvider } from '@/contexts/EmailAccountContext';
import { MartinaGlobalProvider } from '@/contexts/ExAiGlobalContext';
import '@/utils/webglOptimization';


// Lazy loading delle pagine
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const GoogleCallbackBridge = lazy(() => import('@/pages/auth/GoogleCallbackBridge'));
const MartinaDashboardPage = lazy(() => import('@/pages/dashboard/MartinaDashboard'));
const FeedPage = lazy(() => import('@/pages/FeedPage'));
const GlobalSearchResultsPage = lazy(() => import('@/pages/search'));

// Contacts
const ContactsPage = lazy(() => import('@/pages/contacts'));
const ContactDetailPage = lazy(() => import('@/pages/contacts/[id]'));

// Companies
const CompaniesPage = lazy(() => import('@/pages/companies'));
const CompanyDetailPage = lazy(() => import('@/pages/companies/[id]'));

// Deals
const DealsPage = lazy(() => import('@/pages/deals/DealsPage'));
const DealDetailPage = lazy(() => import('@/pages/deals/[id]'));

// Email
const EmailPage = lazy(() => import('@/pages/emails'));
const EmailInboxPage = lazy(() => import('@/pages/emails/inbox'));
const EmailSettingsPage = lazy(() => import('@/pages/emails/settings'));
const EmailSignaturesPage = lazy(() => import('@/pages/emails/signatures'));

function EmailComposeRedirect() {
  const [, navigate] = useLocation();
  useEffect(() => navigate('/emails?compose=1'), [navigate]);
  return null;
}

// Fusione dashboard (19/08): /dashboard-overview e' stata assorbita nella
// dashboard unica su "/". Redirect invece di 404 per bookmark/link esistenti.
function LegacyDashboardOverviewRedirect() {
  const [, navigate] = useLocation();
  useEffect(() => navigate('/'), [navigate]);
  return null;
}

// Admin
const MaintenancePage = lazy(() => import('@/pages/admin/Maintenance'));

// Other modules
// Import esplicito di leads/index.tsx (tabella ridisegnata + RecordDrawer):
// senza /index il resolver su FS case-insensitive cattura pages/Leads.tsx (wrapper AutoLeadsList).
const LeadsPage = lazy(() => import('@/pages/leads/index'));
const SearchAndEditPage = lazy(() => import('@/pages/ricerca-modifica/index'));
const SynergiesPage = lazy(() => import('@/pages/synergies'));
const TasksPage = lazy(() => import('@/pages/tasks'));
const MeetingsPage = lazy(() => import('@/pages/meetings'));
const AssociationsPage = lazy(() => import('@/pages/AssociationsPage'));
const DeduplicationPage = lazy(() => import('@/pages/deduplication'));
const ManualMergePage = lazy(() => import('@/pages/ManualMergePage'));
const ActivitiesPage = lazy(() => import('@/pages/activities'));
const CalendarPage = lazy(() => import('@/pages/calendar'));
const NotebookPage = lazy(() => import('@/pages/notes'));
const TravelModulePage = lazy(() => import('@/pages/travel'));
const OpportunitiesPage = lazy(() => import('@/pages/opportunities'));
const DataExplorerPage = lazy(() => import('@/pages/data-explorer'));
const SettingsPage = lazy(() => import('@/pages/settings'));
const UserAccountPage = lazy(() => import('@/pages/settings/UserAccountPage'));
const AISettingsPage = lazy(() => import('@/pages/settings/ai'));
const ThemeManagementPage = lazy(() => import('@/pages/settings/theme'));
const IntegrationsSettingsPage = lazy(() => import('@/pages/settings/integrations'));
const HelpPage = lazy(() => import('@/pages/help'));
const AiEnhancementPage = lazy(() => import('@/pages/AiEnhancement'));
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'));
const NotificationsTestPage = lazy(() => import('@/pages/NotificationsTestPage'));
const TestAssociationsPage = lazy(() => import('@/pages/TestAssociationsPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const SystemDebugPage = lazy(() => import('@/pages/system-debug'));
const CodeCompletionPage = lazy(() => import('@/pages/code-completion'));

function LegacyEmailAccountRedirect({ mode }: { mode: 'list' | 'add' | 'edit' }) {
  const [, navigate] = useLocation();
  const [, params] = useRoute<{ id: string }>('/emails/accounts/edit/:id');

  useEffect(() => {
    const suffix = mode === 'add'
      ? '&emailView=add'
      : mode === 'edit' && params?.id
        ? `&emailView=edit&accountId=${params.id}`
        : '';
    navigate(`/settings?tab=email${suffix}`);
  }, [mode, navigate, params?.id]);

  return null;
}

// Branches module: Filiali legacy/future punti vendita, separato dalle Company controllate.
const BranchesPage = lazy(() => import('@/pages/branches'));
const BranchDetailPage = lazy(() => import('@/pages/branches/[id]'));

// Brands module
const BrandsPage = lazy(() => import('@/pages/brands'));
const BrandDetailPage = lazy(() => import('@/pages/brands/[id]'));
const FilesPage = lazy(() => import('@/pages/files/index'));

// Componente di caricamento
function LoadingScreen() {
  return (
    <div className="flex h-screen w-full items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-2">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
        <p className="text-sm text-muted-foreground">Caricamento...</p>
      </div>
    </div>
  );
}

// Pagina con layout applicazione
function LayoutPage({ component: Component }: { component: React.ComponentType }) {
  return (
    <AppLayout>
      <Component />
    </AppLayout>
  );
}

// Fjord owns the presentation. Legacy preferences remain inert for Git rollback.
function ThemeInitializer() {
  useEffect(() => {
    const activeTheme = document.documentElement.dataset.ui === 'fjord' ? 'fjord' : 'experviser';
    document.documentElement.dataset.theme = activeTheme;
    document.body.dataset.theme = activeTheme;
  }, []);
  return null;
}

// Componente wrapper per gestire l'aggiornamento della cache
function CacheReloader() {
  const { toast } = useToast();
  
  useEffect(() => {
    // Funzione per gestire gli eventi di localStorage
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'cache_purge_timestamp') {
        // Forza il ricaricamento completo dei dati
        clearCacheAndReloadData().then(() => {
          toast({
            title: 'Aggiornamento dati',
            description: 'I dati sono stati ricaricati dal server.',
          });
        });
      }
    };
    
    // Aggiungi un listener per gli eventi di storage
    window.addEventListener('storage', handleStorageChange);
    
    // Cleanup del listener quando il componente viene smontato
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [toast]);
  
  return null;
}

function MartinaPushToaster() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'MARTINA_PUSH') return;
      const payload = event.data.payload ?? {};
      const rawTarget = String(payload.deepLink || payload.data?.deepLink || payload.url || '/');
      const target = canonicalizeNotificationLink(rawTarget) || '/';
      const payloadData = payload.data && typeof payload.data === 'object' ? payload.data : {};
      const roomId = Number(payload.roomId ?? payloadData.roomId);
      const isRoomPush = (Number.isSafeInteger(roomId) && roomId > 0) || target.includes('room=');
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: MARTINA_OPERATIONAL_QUEUE_COUNT_QUERY_KEY });
      if (isRoomPush) return;
      toast({
        title: String(payload.title || 'Martina'),
        description: String(payload.body || 'Hai un aggiornamento nel CRM.'),
        action: (
          <ToastAction altText="Apri notifica Martina" onClick={() => navigate(target)}>
            Apri
          </ToastAction>
        ),
      });
    };
    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleMessage);
  }, [navigate, queryClient, toast]);

  return null;
}

function AppServiceWorkerBootstrap() {
  useEffect(() => {
    if (!('serviceWorker' in navigator) || !window.isSecureContext) return;
    navigator.serviceWorker.register('/martina-sw.js', { scope: '/' }).catch((error) => {
      console.warn('[PWA] Service worker non registrato', error);
    });
  }, []);
  return null;
}

function AppContent() {
  return (
      <EmailAccountProvider>
      {/* Componente per notifiche toast */}
      <Toaster />
      <AppServiceWorkerBootstrap />

      <MartinaGlobalProvider>

      {/* Pulsante di emergenza per la Debug Console */}
      <EmergencyDebugButton />
      
      {/* Componente per gestire il ricaricamento della cache */}
      <CacheReloader />
      <MartinaPushToaster />
      
      {/* Inizializzazione del tema salvato */}
      <ThemeInitializer />
      
      <Suspense fallback={<LoadingScreen />}>
        <Switch>
          {/* Pagina di login (senza layout AppLayout) */}
          <Route path="/login" component={LoginPage} />
          {/* Aggiungiamo anche il percorso alternativo per il login */}
          <Route path="/auth/login" component={LoginPage} />
          {/* Bridge di completamento login/collegamento con Google */}
          <Route path="/auth/google/callback" component={GoogleCallbackBridge} />
          
          {/* Pagine protette (con layout AppLayout) */}
          <Route path="/search">
            <ProtectedRoute>
              <LayoutPage component={GlobalSearchResultsPage} />
            </ProtectedRoute>
          </Route>
          <Route path="/dashboard">
            <ProtectedRoute>
              <LayoutPage component={MartinaDashboardPage} />
            </ProtectedRoute>
          </Route>
          <Route path="/">
            <ProtectedRoute>
              <LayoutPage component={FeedPage} />
            </ProtectedRoute>
          </Route>
          <Route path="/dashboard-overview">
            <ProtectedRoute>
              <LegacyDashboardOverviewRedirect />
            </ProtectedRoute>
          </Route>
          
          {/* Routes for Contacts */}
          <Route path="/contacts/new">
            <ProtectedRoute>
              <LayoutPage component={ContactsPage} />
            </ProtectedRoute>
          </Route>
          <Route path="/contacts/:id">
            <ProtectedRoute>
              <LayoutPage component={ContactDetailPage} />
            </ProtectedRoute>
          </Route>
          <Route path="/contacts">
            <ProtectedRoute>
              <LayoutPage component={ContactsPage} />
            </ProtectedRoute>
          </Route>
          
          {/* Routes for Companies */}
          <Route path="/companies">
            <ProtectedRoute>
              <LayoutPage component={CompaniesPage} />
            </ProtectedRoute>
          </Route>
          <Route path="/companies/:id">
            <ProtectedRoute>
              <LayoutPage component={CompanyDetailPage} />
            </ProtectedRoute>
          </Route>
          
          {/* Routes for Deals */}
          <Route path="/deals">
            <ProtectedRoute>
              <LayoutPage component={DealsPage} />
            </ProtectedRoute>
          </Route>
          <Route path="/deals/new">
            <ProtectedRoute>
              <LayoutPage component={lazy(() => import('@/pages/deals/new'))} />
            </ProtectedRoute>
          </Route>
          <Route path="/deals/:id">
            <ProtectedRoute>
              <LayoutPage component={DealDetailPage} />
            </ProtectedRoute>
          </Route>
          
          {/* Routes for Leads */}
          <Route path="/leads">
            <ProtectedRoute>
              <LayoutPage component={LeadsPage} />
            </ProtectedRoute>
          </Route>
          <Route path="/leads/:id">
            <ProtectedRoute>
              <LayoutPage component={lazy(() => import('@/pages/leads/[id]'))} />
            </ProtectedRoute>
          </Route>

          {/* Ricerca e modifica: modulo dedicato, separato dagli altri —
              costruttore di condizioni E/O su qualsiasi modulo, collegato
              al motore unico di modifica di massa. */}
          <Route path="/ricerca">
            <ProtectedRoute>
              <LayoutPage component={SearchAndEditPage} />
            </ProtectedRoute>
          </Route>

          {/* Route for Synergies */}
          <Route path="/synergies">
            <ProtectedRoute>
              <LayoutPage component={SynergiesPage} />
            </ProtectedRoute>
          </Route>
          
          <Route path="/notes/:id"><ProtectedRoute><LayoutPage component={NotebookPage} /></ProtectedRoute></Route>
          <Route path="/notes"><ProtectedRoute><LayoutPage component={NotebookPage} /></ProtectedRoute></Route>
          {/* Route for Tasks */}
          <Route path="/tasks">
            <ProtectedRoute>
              <LayoutPage component={TasksPage} />
            </ProtectedRoute>
          </Route>
          
          {/* Route for Meetings */}
          <Route path="/meetings/:id">
            <ProtectedRoute>
              <LayoutPage component={MeetingsPage} />
            </ProtectedRoute>
          </Route>
          <Route path="/meetings">
            <ProtectedRoute>
              <LayoutPage component={MeetingsPage} />
            </ProtectedRoute>
          </Route>
          
          {/* Route for Activities */}
          <Route path="/activities">
            <ProtectedRoute>
              <LayoutPage component={ActivitiesPage} />
            </ProtectedRoute>
          </Route>
          
          {/* Route for Calendar */}
          <Route path="/calendar">
            <ProtectedRoute>
              <LayoutPage component={CalendarPage} />
            </ProtectedRoute>
          </Route>
          <Route path="/travel">
            <ProtectedRoute>
              <LayoutPage component={TravelModulePage} />
            </ProtectedRoute>
          </Route>
          
          {/* Route for Deduplication */}
          <Route path="/deduplication">
            <ProtectedRoute>
              <LayoutPage component={DeduplicationPage} />
            </ProtectedRoute>
          </Route>
          
          {/* Route for Manual Merge */}
          <Route path="/manual-merge">
            <ProtectedRoute>
              <LayoutPage component={ManualMergePage} />
            </ProtectedRoute>
          </Route>
          
          {/* Legacy duplicate routes removed - using new integrated deduplication system */}
          
          {/* Routes for Email */}
          <Route path="/emails">
            <ProtectedRoute>
              <LayoutPage component={EmailPage} />
            </ProtectedRoute>
          </Route>
          <Route path="/emails/inbox">
            <ProtectedRoute>
              <LayoutPage component={EmailInboxPage} />
            </ProtectedRoute>
          </Route>
          <Route path="/emails/compose">
            <ProtectedRoute>
              <EmailComposeRedirect />
            </ProtectedRoute>
          </Route>
          <Route path="/emails/signatures">
            <ProtectedRoute>
              <LayoutPage component={EmailSignaturesPage} />
            </ProtectedRoute>
          </Route>
          <Route path="/emails/accounts">
            <ProtectedRoute>
              <LegacyEmailAccountRedirect mode="list" />
            </ProtectedRoute>
          </Route>
          <Route path="/emails/accounts/add">
            <ProtectedRoute>
              <LegacyEmailAccountRedirect mode="add" />
            </ProtectedRoute>
          </Route>
          <Route path="/emails/accounts/edit/:id">
            <ProtectedRoute>
              <LegacyEmailAccountRedirect mode="edit" />
            </ProtectedRoute>
          </Route>
          <Route path="/emails/settings">
            <ProtectedRoute>
              <LayoutPage component={EmailSettingsPage} />
            </ProtectedRoute>
          </Route>
          
          {/* Routes for Branches: layer legacy/futuro dei punti vendita. */}
          <Route path="/branches">
            <ProtectedRoute>
              <LayoutPage component={BranchesPage} />
            </ProtectedRoute>
          </Route>
          <Route path="/branches/:id">
            <ProtectedRoute>
              <LayoutPage component={BranchDetailPage} />
            </ProtectedRoute>
          </Route>
          
          {/* Route for Brands */}
          <Route path="/brands/:id">
            <ProtectedRoute>
              <LayoutPage component={BrandDetailPage} />
            </ProtectedRoute>
          </Route>
          <Route path="/brands">
            <ProtectedRoute>
              <LayoutPage component={BrandsPage} />
            </ProtectedRoute>
          </Route>

          {/* Route for Files (destinazione canonica dei link /files?fileId= di Martina) */}
          <Route path="/files">
            <ProtectedRoute>
              <LayoutPage component={FilesPage} />
            </ProtectedRoute>
          </Route>
          
          {/* Routes for Settings */}
          <Route path="/settings">
            <ProtectedRoute>
              <LayoutPage component={SettingsPage} />
            </ProtectedRoute>
          </Route>
          <Route path="/settings/account">
            <ProtectedRoute>
              <LayoutPage component={UserAccountPage} />
            </ProtectedRoute>
          </Route>
          <Route path="/settings/ai">
            <ProtectedRoute>
              <LayoutPage component={AISettingsPage} />
            </ProtectedRoute>
          </Route>
          <Route path="/settings/theme">
            <ProtectedRoute>
              <LayoutPage component={ThemeManagementPage} />
            </ProtectedRoute>
          </Route>
          <Route path="/settings/data-management">
            <ProtectedRoute>
              <LayoutPage component={lazy(() => import('@/pages/settings/DataManagementPage'))} />
            </ProtectedRoute>
          </Route>
          <Route path="/settings/integrations">
            <ProtectedRoute>
              <LayoutPage component={IntegrationsSettingsPage} />
            </ProtectedRoute>
          </Route>
          {/*
            Sezioni delle Impostazioni (redesign hub, 3 ago 2026). DEVE restare
            DOPO le rotte specifiche qui sopra: wouter usa il primo match, e
            quelle pagine esistevano gia' come destinazioni autonome — le card
            dell'hub ci puntano invece di duplicarle.
          */}
          <Route path="/settings/:slug">
            <ProtectedRoute>
              <LayoutPage component={SettingsPage} />
            </ProtectedRoute>
          </Route>

          {/* Import/Export Module - Modulo separato */}
          <Route path="/import-export-module">
            <ProtectedRoute>
              <LayoutPage component={lazy(() => import('@/pages/ImportExportModulePage'))} />
            </ProtectedRoute>
          </Route>
          <Route path="/data-explorer"><ProtectedRoute><LayoutPage component={DataExplorerPage} /></ProtectedRoute></Route>
          
          {/* Route for Help */}
          <Route path="/help">
            <ProtectedRoute>
              <LayoutPage component={HelpPage} />
            </ProtectedRoute>
          </Route>
          
          {/* Route for Maintenance */}
          <Route path="/admin/maintenance">
            <ProtectedRoute>
              <LayoutPage component={MaintenancePage} />
            </ProtectedRoute>
          </Route>
          
          {/* System Debugging Page */}
          <Route path="/system-debug">
            <ProtectedRoute>
              <LayoutPage component={SystemDebugPage} />
            </ProtectedRoute>
          </Route>
          
          {/* AI Enhancement Page */}
          <Route path="/ai-enhancement">
            <ProtectedRoute>
              <LayoutPage component={AiEnhancementPage} />
            </ProtectedRoute>
          </Route>
          
          {/* Code Completion Page */}
          <Route path="/code-completion">
            <ProtectedRoute>
              <LayoutPage component={CodeCompletionPage} />
            </ProtectedRoute>
          </Route>
          
          {/* Debug Test Console Page */}
          <Route path="/debug/test">
            <ProtectedRoute>
              <LayoutPage component={lazy(() => import('@/pages/debug/TestConsole'))} />
            </ProtectedRoute>
          </Route>
          
          {/* Opportunities Page */}
          <Route path="/opportunities">
            <ProtectedRoute>
              <LayoutPage component={OpportunitiesPage} />
            </ProtectedRoute>
          </Route>

          <Route path="/feed">
            <ProtectedRoute>
              <LayoutPage component={FeedPage} />
            </ProtectedRoute>
          </Route>
          
          {/* Notifications Test Page */}
          <Route path="/notifications">
            <ProtectedRoute>
              <LayoutPage component={NotificationsPage} />
            </ProtectedRoute>
          </Route>

          {/* Notifications Test Page */}
          <Route path="/test-notifications">
            <ProtectedRoute>
              <LayoutPage component={NotificationsTestPage} />
            </ProtectedRoute>
          </Route>
          
          {/* Associations Test Page */}
          <Route path="/test-associations">
            <ProtectedRoute>
              <LayoutPage component={TestAssociationsPage} />
            </ProtectedRoute>
          </Route>
          
          {/* Contact-Company Associations Management Page */}
          <Route path="/associations">
            <ProtectedRoute>
              <LayoutPage component={AssociationsPage} />
            </ProtectedRoute>
          </Route>
          
          {/* Pagina non trovata - catch all route */}
          <Route>
            <ProtectedRoute>
              <LayoutPage component={NotFoundPage} />
            </ProtectedRoute>
          </Route>
        </Switch>
      </Suspense>
      </MartinaGlobalProvider>
      </EmailAccountProvider>
  );
}

const crmQueryPersister = createCrmQueryPersister();

export default function App() {
  useEffect(() => {
    let restoringCancelledBack = false;
    const guardBrowserBack = (event: PopStateEvent) => {
      if (restoringCancelledBack) {
        restoringCancelledBack = false;
        return;
      }
      if (allowNavigationWithUnsavedChanges()) return;
      event.stopImmediatePropagation();
      restoringCancelledBack = true;
      window.history.forward();
    };
    window.addEventListener('popstate', guardBrowserBack, true);
    return () => window.removeEventListener('popstate', guardBrowserBack, true);
  }, []);
  if (!crmQueryPersister) {
    return <QueryClientProvider client={queryClient}><Router aroundNav={(navigate, to, options) => { if (allowNavigationWithUnsavedChanges()) navigate(to, options); }}><AppContent /></Router></QueryClientProvider>;
  }
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: crmQueryPersister,
        maxAge: CRM_QUERY_CACHE_MAX_AGE_MS,
        buster: CRM_QUERY_CACHE_BUSTER,
        dehydrateOptions: { shouldDehydrateQuery: shouldPersistCrmQuery },
      }}
    >
      <Router aroundNav={(navigate, to, options) => { if (allowNavigationWithUnsavedChanges()) navigate(to, options); }}><AppContent /></Router>
    </PersistQueryClientProvider>
  );
}
